/**
 * Low-level query-param key primitives, reserved for the route-query-state
 * layer (`src/hooks/useRouteQueryState/**`), which is the single owner of URL
 * query-state writes. These are deliberately kept OUT of the module's public
 * surface (`constants.ts`) and importing this file is lint-blocked
 * (`no-restricted-imports` in `eslint.config.js`) everywhere else: every other
 * consumer composes keys through `useRouteQueryState`'s `queryState`/
 * `queryActions` contract rather than building `pn-*` keys by hand. The safe
 * read-side predicates live in `constants.ts`, which is freely importable.
 */

const env = import.meta.env;

export const FILTER_PREFIX = (env.VITE_QUERY_PARAM_PREFIX_FILTER ??
  'pn-f-') as string;
export const COLUMN_PREFIX = (env.VITE_QUERY_PARAM_PREFIX_COLUMN ??
  'pn-c-') as string;
export const PAGINATION_PREFIX = (env.VITE_QUERY_PARAM_PREFIX_PAGINATION ??
  'pn-p-') as string;
export const SORT_PREFIX = (env.VITE_QUERY_PARAM_PREFIX_SORT ??
  'pn-s-') as string;

export const CACHEABLE_PREFIXES = [
  FILTER_PREFIX,
  COLUMN_PREFIX,
  PAGINATION_PREFIX,
  SORT_PREFIX
] as const;

/**
 * Trailing sentinel appended to a query key to mark it URL-shareable but never
 * cached. The route-agnostic writer (usePersistedQueryParams) only sees key
 * strings, so a per-key "do not persist" decision has to ride along inside the
 * key itself — this marker is that transport.
 */
export const EPHEMERAL_MARKER = '~';

export const composeKey = (
  prefix: string,
  name: string,
  suffix?: string,
  options: { ephemeral?: boolean } = {}
): string => {
  const base = suffix ? `${prefix}${name}-${suffix}` : `${prefix}${name}`;
  return options.ephemeral ? `${base}${EPHEMERAL_MARKER}` : base;
};
